import React, { useEffect, useState } from "react";
import { Button, Col, Input, Label, Row } from "reactstrap";
import { Link, Redirect, useHistory, useParams } from "react-router-dom";
import { getApi, putApi } from "../../helpers/auth_helpers";
import { STUDIO_MODULE_IDS } from "../../helpers/studioModules";
import notifications from "../../common/Components/Notifications/Notification";
import Loader from "../../common/Components/Loader/Loader";

const toDateInput = (raw: any): string => {
  if (!raw) {
    return "";
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const SuperAdminOrganisationPage = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState<any>(null);
  const [catalog, setCatalog] = useState<any[]>([]);
  const [rows, setRows] = useState<Array<{ id: string; enabled: boolean; accessUntil: string }>>([]);

  const load = (organisationId: string) => {
    setLoading(true);
    getApi(`${process.env.REACT_APP_SERVERURL}/organization/${organisationId}`).then((res: any) => {
      const found = res?.success ? res.data : null;
      setOrg(found);
      getApi(`${process.env.REACT_APP_SERVERURL}/saas/catalog`).then((cat: any) => {
        const modules = cat?.success ? cat.data?.modules || [] : [];
        setCatalog(modules);
        const ids = modules.length ? modules.map((m: any) => m.id) : [...STUDIO_MODULE_IDS];
        const grants = found?.moduleGrants || [];
        const optedOut = new Set(
          (found?.optedOutModules || []).map((x: any) => String(x).toUpperCase())
        );
        const entitled = new Set((found?.enabledModules || []).map((x: any) => String(x).toUpperCase()));
        setRows(
          ids.map((moduleId: string) => {
            const grant = grants.find(
              (g: any) =>
                g?.module &&
                String(g.module).toUpperCase() === moduleId &&
                g.source &&
                g.source !== "TRIAL" &&
                (!g.endsAt || new Date(g.endsAt).getTime() > Date.now())
            );
            const on = entitled.has(moduleId) || (Boolean(grant) && !optedOut.has(moduleId));
            return {
              id: moduleId,
              enabled: on,
              accessUntil: toDateInput(grant?.endsAt),
            };
          })
        );
        setLoading(false);
      });
    });
  };

  useEffect(() => {
    if (id) {
      load(id);
    }
  }, [id]);

  if (authUser?.users?.role !== "SUPER_ADMIN") {
    return <Redirect to="/organisation" />;
  }

  const setRow = (moduleId: string, patch: Partial<{ enabled: boolean; accessUntil: string }>) => {
    setRows((prev) =>
      prev.map((row) => (row.id === moduleId ? { ...row, ...patch } : row))
    );
  };

  const save = async () => {
    if (!id) {
      return;
    }
    setSaving(true);
    try {
      const res: any = await putApi(`${process.env.REACT_APP_SERVERURL}/organization/${id}/modules`, {
        modules: rows.map((row) => ({
          id: row.id,
          enabled: row.enabled,
          accessUntil: row.enabled && row.accessUntil ? row.accessUntil : null,
        })),
      });
      if (res?.success) {
        notifications({ title: "Saved", message: "Module access updated for this organisation.", type: "success" });
        load(id);
      } else {
        notifications({ title: "Error", message: res?.message || "Could not save modules", type: "danger" });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader />;
  }
  if (!org) {
    return (
      <div className="p-4">
        <p>Organisation not found.</p>
        <Button color="secondary" outline onClick={() => history.push("/organisation")}>
          Back to list
        </Button>
      </div>
    );
  }

  const labelFor = (moduleId: string) =>
    catalog.find((m: any) => m.id === moduleId)?.label || moduleId;

  return (
    <div className="AddArtform studio-admin-org-page">
      <p className="mb-3">
        <Link to="/organisation">Organisations</Link>
      </p>
      <div className="d-flex justify-content-center">
        <div className="imageDiv">
          {org?.logo ? (
            <img src={org.logo} alt="" width="200px" height="200px" />
          ) : (
            <div>No Preview Available</div>
          )}
        </div>
      </div>
      <div className="form">
        <div className="formDiv">
          <Label>Organization</Label>
          <Input value={org?.name || ""} readOnly />
        </div>
        <div className="formDiv">
          <Label>Address</Label>
          <textarea className="form-control" value={org?.address?.addressLane || ""} readOnly />
        </div>
        <div className="formDiv">
          <Row>
            <Col xs={12} md={4}>
              <Label>City</Label>
              <Input value={org?.address?.city || ""} readOnly />
            </Col>
            <Col xs={12} md={4}>
              <Label>State</Label>
              <Input value={org?.address?.state || ""} readOnly />
            </Col>
            <Col xs={12} md={4}>
              <Label>Pin code</Label>
              <Input value={org?.address?.pinCode || ""} readOnly />
            </Col>
          </Row>
        </div>
        <div className="formDiv">
          <Row>
            <Col xs={12} md={6}>
              <Label>Phone</Label>
              <Input value={org?.phone || ""} readOnly />
            </Col>
            <Col xs={12} md={6}>
              <Label>Type</Label>
              <Input value={org?.organizationType || ""} readOnly />
            </Col>
          </Row>
        </div>
        <div className="formDiv">
          <Row>
            <Col xs={12} md={6}>
              <Label>GST</Label>
              <Input value={org?.gst || "NA"} readOnly />
            </Col>
            <Col xs={12} md={6}>
              <Label>TAN</Label>
              <Input value={org?.tan || "NA"} readOnly />
            </Col>
          </Row>
        </div>
      </div>

      <div className="form mt-4">
        <h4>Studio modules</h4>
        <p>
          Turn each module on or off for this organisation. Access until is the last day they can use it.
          Leave the date empty for no end date.
          {org?.trialEndsAt
            ? ` Trial currently ends ${new Date(org.trialEndsAt).toLocaleDateString()}.`
            : " Trial is not set for this organisation."}
        </p>
        {rows.map((row) => (
          <div key={row.id} className="studio-admin-org-page__module">
            <Label check className="studio-admin-org-page__toggle">
              <Input
                type="checkbox"
                checked={row.enabled}
                onChange={(e) => setRow(row.id, { enabled: e.target.checked })}
              />{" "}
              {labelFor(row.id)}
            </Label>
            <div className="studio-admin-org-page__until">
              <Label>Access until</Label>
              <Input
                type="date"
                disabled={!row.enabled}
                value={row.accessUntil}
                onChange={(e) => setRow(row.id, { accessUntil: e.target.value })}
              />
              <span className="studio-admin-org-page__hint">
                {row.enabled
                  ? row.accessUntil
                    ? `On through ${new Date(row.accessUntil).toLocaleDateString()}`
                    : "No end date"
                  : "Off"}
              </span>
            </div>
          </div>
        ))}
        <Button className="mt-3" color="primary" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save module access"}
        </Button>
      </div>
    </div>
  );
};

export default SuperAdminOrganisationPage;
